import { useEffect } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useDriversStore } from '../../stores/useDriversStore';
import { useLoadingStore } from '../../stores/useLoadingStore';

import {
    loadDrivers,
    finishDriversRequest,
    clearDriversRequests,
    isDriversRequestActive
} from '../../services/driversService';

import { loadLines } from '../../services/linesService';

// =========================
// REALTIME PENDENTE
// =========================

const pendingDriverEvents = [];

let pendingInvalidate = false;

export function useDriversSocket() {

    const socket = useSocketStore(
        state => state.socket
    );

    useEffect(() => {

        if (!socket) {
            return;
        }

        const {
            setDrivers,
            upsertDriver,
            removeDriver
        } = useDriversStore.getState();

        const {
            setLoadProgress
        } = useLoadingStore.getState();

        // =========================
        // PROGRESSO
        // =========================

        const handleProgress = ({
            requestId,
            percentage
        }) => {

            if (!isDriversRequestActive(requestId)) {
                return;
            }

            setLoadProgress(percentage ?? 0);

        };

        // =========================
        // DONE
        // =========================

        const handleDone = ({
            requestId,
            drivers
        }) => {

            const isActive = isDriversRequestActive(requestId);

            if (!isActive) {
                return;
            }

            // =========================
            // INSTALA MOTORISTAS
            // =========================

            setDrivers(drivers || []);

            // =========================
            // FINALIZA REQUEST
            // =========================

            setLoadProgress(100);

            finishDriversRequest(requestId);

            // =========================
            // AINDA EXISTEM MOTORISTAS
            // SENDO CARREGADOS
            // =========================

            const {
                loadingDrivers
            } = useLoadingStore.getState();

            if (loadingDrivers) {
                return;
            }

            // =========================
            // APLICA REALTIME PENDENTE
            // =========================

            flushPendingEvents();

            // =========================
            // INVALIDATE PENDENTE
            // =========================

            if (pendingInvalidate) {

                pendingInvalidate = false;

                loadDrivers(socket);

                return;

            }

            // =========================
            // PRÓXIMA ETAPA
            // =========================

            loadLines(socket);

        };

        // =========================
        // ERROR
        // =========================

        const handleError = ({
            requestId
        }) => {

            const isActive = isDriversRequestActive(requestId);

            finishDriversRequest(requestId);

            if (!isActive) {
                return;
            }

            console.error('Erro ao carregar motoristas');

            setLoadProgress(100);

        };

        // =========================
        // APLICA EVENTO REALTIME
        // =========================

        const applyEvent = ({
            type,
            payload
        }) => {

            switch (type) {

                case 'sync':

                    setDrivers(payload || []);

                    break;

                case 'upsert':

                    upsertDriver(payload);

                    break;

                case 'delete':

                    removeDriver(payload);

                    break;

                default:
                    break;

            }

        };

        // =========================
        // RECEBE EVENTO REALTIME
        // =========================

        const handleEvent = (
            type,
            payload
        ) => {

            const {
                loadingDrivers
            } = useLoadingStore.getState();

            if (loadingDrivers) {

                pendingDriverEvents.push({
                    type,
                    payload
                });

                return;

            }

            applyEvent({
                type,
                payload
            });

        };

        // =========================
        // FLUSH REALTIME
        // =========================

        const flushPendingEvents = () => {

            if (!pendingDriverEvents.length) {
                return;
            }

            const events =
                pendingDriverEvents.splice(
                    0,
                    pendingDriverEvents.length
                );

            for (const event of events) {

                applyEvent(event);

            }

        };

        // =========================
        // RELOAD
        // =========================

        const handleReload = () => {

            const {
                loadingDrivers
            } = useLoadingStore.getState();

            if (loadingDrivers) {

                pendingInvalidate = true;

                return;

            }

            loadDrivers(socket);

        };

        // =========================
        // REALTIME
        // =========================

        const handleSync = drivers => {

            handleEvent('sync', drivers);

        };

        const handleUpsert = driver => {

            handleEvent('upsert', driver);

        };

        const handleDelete = driverId => {

            handleEvent('delete', driverId);

        };

        const handleInvalidate = () => {

            handleReload();

        };

        // =========================
        // DISCONNECT
        // =========================

        const handleDisconnect = () => {

            pendingDriverEvents.length = 0;

            pendingInvalidate = false;

            clearDriversRequests();

        };

        // =========================
        // LISTENERS
        // =========================

        socket.on('disconnect', handleDisconnect);

        socket.on('drivers:progress', handleProgress);

        socket.on('drivers:done', handleDone);

        socket.on('drivers:error', handleError);

        socket.on('drivers:sync', handleSync);

        socket.on('driver:upserted', handleUpsert);

        socket.on('driver:deleted', handleDelete);

        socket.on('drivers:invalidate', handleInvalidate);

        return () => {

            socket.off('disconnect', handleDisconnect);

            socket.off('drivers:progress', handleProgress);

            socket.off('drivers:done', handleDone);

            socket.off('drivers:error', handleError);

            socket.off('drivers:sync', handleSync);

            socket.off('driver:upserted', handleUpsert);

            socket.off('driver:deleted', handleDelete);

            socket.off('drivers:invalidate', handleInvalidate);

            pendingDriverEvents.length = 0;

            pendingInvalidate = false;

        };

    }, [socket]);

}